/*
 * Forventet levering: the On-hand / Leverance split of a chosen quantity and its Danish text.
 * Pure functions, no DOM and no clock: "today" comes from the page (snippets/forventet-levering.liquid, mode 'json').
 * Exposed as window.ForventetLevering in the browser and as module.exports in node
 * (worker/scripts/check-forventet-levering.mjs).
 * The Ship day rule is the same as shipDay in worker/src/leverancer/calendar.js and as snippets/forventet-levering-calc.liquid.
 * Dates are ISO yyyy-mm-dd strings and are compared as strings.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.ForventetLevering = api;
})(typeof self !== 'undefined' ? self : this, function () {
  const MAX_SCAN_DAYS = 3660;
  const BASE_INTERVAL = [1, 2];
  const WEEKDAYS = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
  const MONTHS = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];
  const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  const PROPERTY_KEY = 'Forventet levering';

  function toDate(isoDate) {
    return new Date(isoDate + 'T00:00:00Z');
  }

  function addDays(isoDate, days) {
    const date = toDate(isoDate);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  function isWeekday(isoDate) {
    const day = toDate(isoDate).getUTCDay();
    return day !== 0 && day !== 6;
  }

  function isIsoDate(value) {
    if (typeof value !== 'string' || !ISO_DATE.test(value)) return false;
    const date = toDate(value);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }

  /** A closure with both dates and an end on or after its start. */
  function isValidClosure(closure) {
    return !!closure && isIsoDate(closure.startDate) && isIsoDate(closure.endDate) && closure.endDate >= closure.startDate;
  }

  function isClosed(isoDate, closures) {
    return closures.some((c) => isValidClosure(c) && c.startDate <= isoDate && isoDate <= c.endDate);
  }

  /** A weekday outside every valid closure. */
  function isWorkingDay(isoDate, closures) {
    return isWeekday(isoDate) && !isClosed(isoDate, closures);
  }

  function firstWorkingDayFrom(isoDate, closures) {
    let date = isoDate;
    for (let scanned = 0; !isWorkingDay(date, closures); scanned++) {
      if (scanned > MAX_SCAN_DAYS) throw new Error('ship_day_out_of_range');
      date = addDays(date, 1);
    }
    return date;
  }

  function addWeekdays(isoDate, count) {
    let date = isoDate;
    let remaining = Number(count) || 0;
    for (let scanned = 0; remaining > 0; scanned++) {
      if (scanned > MAX_SCAN_DAYS) throw new Error('ship_day_out_of_range');
      date = addDays(date, 1);
      if (isWeekday(date)) remaining--;
    }
    return date;
  }

  /** The first Working day on or after (arrival date + buffer weekdays). */
  function shipDay(leverance, closures) {
    return firstWorkingDayFrom(addWeekdays(leverance.arrivalDate, leverance.bufferDays), closures);
  }

  /**
   * The Ship day plus Transit days in weekdays. A Ship day before `today` is replaced by the
   * first Working day after `today`.
   */
  function deliveryDay(leverance, closures, today) {
    let ship = shipDay(leverance, closures);
    if (ship < today) ship = firstWorkingDayFrom(addDays(today, 1), closures);
    return addWeekdays(ship, leverance.transitDays == null ? 1 : leverance.transitDays);
  }

  /**
   * The On-hand interval in weekdays: [1, 2], or [1 + n, 2 + n] when today is inside a closure,
   * n being the weekdays from today until the next Working day.
   */
  function onHandInterval(closures, today) {
    if (!isClosed(today, closures)) return BASE_INTERVAL.slice();
    let closedWeekdays = 0;
    let date = today;
    for (let scanned = 0; !isWorkingDay(date, closures); scanned++) {
      if (scanned > MAX_SCAN_DAYS) throw new Error('ship_day_out_of_range');
      if (isWeekday(date)) closedWeekdays++;
      date = addDays(date, 1);
    }
    return [BASE_INTERVAL[0] + closedWeekdays, BASE_INTERVAL[1] + closedWeekdays];
  }

  function formatLong(isoDate, today) {
    const date = toDate(isoDate);
    const text = WEEKDAYS[date.getUTCDay()] + ' ' + date.getUTCDate() + '. ' + MONTHS[date.getUTCMonth()];
    return isoDate.slice(0, 4) === today.slice(0, 4) ? text : text + ' ' + isoDate.slice(0, 4);
  }

  function formatShort(isoDate, today) {
    const date = toDate(isoDate);
    const text = date.getUTCDate() + '. ' + MONTHS_SHORT[date.getUTCMonth()];
    return isoDate.slice(0, 4) === today.slice(0, 4) ? text : text + ' ' + isoDate.slice(0, 4);
  }

  function wholeNumber(value) {
    const n = Math.floor(Number(value));
    return Number.isFinite(n) ? n : 0;
  }

  /** The rows with open units and a valid arrival date; the others are not counted. */
  function countedRows(data) {
    return (data.rows || []).filter((row) => isIsoDate(row.arrivalDate) && wholeNumber(row.openQuantity) > 0);
  }

  /**
   * The sellable Leverance units per Delivery day, soonest first. Rows on the same Delivery day are merged.
   * When Shopify's stock is below the open units, the shortfall is taken from the soonest days first.
   */
  function incomingParts(data) {
    const closures = data.closures || [];
    const byDay = new Map();
    let openTotal = 0;
    for (const row of countedRows(data)) {
      const units = wholeNumber(row.openQuantity);
      const day = deliveryDay(row, closures, data.today);
      byDay.set(day, (byDay.get(day) || 0) + units);
      openTotal += units;
    }
    const parts = Array.from(byDay, ([deliveryDay, units]) => ({ deliveryDay, units })).sort((a, b) =>
      a.deliveryDay < b.deliveryDay ? -1 : a.deliveryDay > b.deliveryDay ? 1 : 0
    );
    let shortfall = Math.max(0, openTotal - wholeNumber(data.stock));
    for (const part of parts) {
      const taken = Math.min(part.units, shortfall);
      part.units -= taken;
      shortfall -= taken;
    }
    return parts.filter((part) => part.units > 0);
  }

  /**
   * The split of `quantity` for one variant.
   * @param {{ today: string, tracked: boolean, stock: number,
   *   rows: { arrivalDate: string, bufferDays: number, transitDays: number, openQuantity: number }[],
   *   closures: { startDate: string, endDate: string }[] }} data
   * @param {number} quantity
   * @returns {{ onHand: number|null, incoming: number, available: number|null, uncovered: number,
   *   interval: number[], parts: { kind: 'on_hand'|'leverance', units: number, deliveryDay?: string }[],
   *   soonestDeliveryDay: string|null, text: string }}
   */
  function forVariant(data, quantity) {
    const today = data.today;
    const interval = onHandInterval(data.closures || [], today);
    const intervalText = interval[0] + '-' + interval[1] + ' hverdage';
    const wanted = Math.max(1, wholeNumber(quantity));

    if (data.tracked === false) {
      return {
        onHand: null,
        incoming: 0,
        available: null,
        uncovered: 0,
        interval,
        parts: [{ kind: 'on_hand', units: wanted }],
        soonestDeliveryDay: null,
        text: intervalText,
      };
    }

    const incoming = incomingParts(data);
    const incomingUnits = incoming.reduce((sum, part) => sum + part.units, 0);
    const openTotal = countedRows(data).reduce((sum, row) => sum + wholeNumber(row.openQuantity), 0);
    const onHand = Math.max(0, wholeNumber(data.stock) - openTotal);
    const available = onHand + incomingUnits;

    const parts = [];
    let remaining = wanted;
    if (onHand > 0) {
      const units = Math.min(onHand, remaining);
      parts.push({ kind: 'on_hand', units });
      remaining -= units;
    }
    for (const part of incoming) {
      if (remaining <= 0) break;
      const units = Math.min(part.units, remaining);
      parts.push({ kind: 'leverance', units, deliveryDay: part.deliveryDay });
      remaining -= units;
    }

    let text;
    if (available === 0) text = 'Udsolgt';
    else if (parts.length === 1 && parts[0].kind === 'on_hand') text = intervalText;
    else {
      text = parts
        .map((part) =>
          part.kind === 'on_hand'
            ? part.units + ' stk ' + intervalText
            : part.units + ' stk forventes ' + formatLong(part.deliveryDay, today)
        )
        .join(' · ');
    }

    return {
      onHand,
      incoming: incomingUnits,
      available,
      uncovered: remaining,
      interval,
      parts,
      soonestDeliveryDay: incoming.length ? incoming[0].deliveryDay : null,
      text,
    };
  }

  /**
   * The "Forventet levering" line property value for a cart line of `quantity` units: the split text when
   * the quantity is above On-hand, '' when it is within On-hand, the variant is untracked, nothing is
   * on hand or incoming, or the quantity is above On-hand plus open Leverance units.
   * @param {object} data - the 'json' output of snippets/forventet-levering.liquid
   * @param {number} quantity
   * @returns {string}
   */
  function lineText(data, quantity) {
    const result = forVariant(data, quantity);
    if (result.onHand === null || result.available === 0 || result.uncovered > 0) return '';
    if (Math.max(1, wholeNumber(quantity)) <= result.onHand) return '';
    return result.text;
  }

  /**
   * The line properties a cart line should carry for `text`, and how to write them with the Cart Ajax API:
   * 'none' when they equal `current`, 'set' for /cart/change.js with `properties`, 'readd' when the line
   * must lose every property (remove the line and add it again without properties).
   * @param {object|null} current - the line's properties now
   * @param {string} text - from lineText; '' drops the property
   * @returns {{ action: 'none'|'set'|'readd', properties: object }}
   */
  function propertyWrite(current, text) {
    const now = current || {};
    const properties = {};
    for (const key of Object.keys(now)) {
      if (key !== PROPERTY_KEY) properties[key] = now[key];
    }
    if (text) properties[PROPERTY_KEY] = text;

    const nowKeys = Object.keys(now);
    const nextKeys = Object.keys(properties);
    const same = nowKeys.length === nextKeys.length && nextKeys.every((key) => now[key] === properties[key]);
    if (same) return { action: 'none', properties };
    if (nextKeys.length === 0) return { action: 'readd', properties };
    return { action: 'set', properties };
  }

  /**
   * How a cart line of `quantity` units is written: the quantity it should have and its properties.
   * A tracked variant with nothing on hand or incoming keeps its quantity and properties ('none'). A
   * quantity above On-hand plus open Leverance units is lowered to that number. The properties follow
   * propertyWrite for lineText at the resulting quantity.
   * @param {object} data - the 'json' output of snippets/forventet-levering.liquid
   * @param {object|null} current - the line's properties now
   * @param {number} quantity
   * @returns {{ action: 'none'|'set'|'readd', properties: object, quantity: number }}
   */
  function cartLineWrite(data, current, quantity) {
    const result = forVariant(data, quantity);
    if (result.onHand !== null && result.available === 0) {
      return { action: 'none', properties: current || {}, quantity };
    }
    const target = result.onHand !== null && result.uncovered > 0 ? result.available : quantity;
    return { ...propertyWrite(current, lineText(data, target)), quantity: target };
  }

  return {
    PROPERTY_KEY,
    lineText,
    propertyWrite,
    cartLineWrite,
    isWorkingDay,
    shipDay,
    deliveryDay,
    onHandInterval,
    formatLong,
    formatShort,
    forVariant,
  };
});
